import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { CheckCircle2, XCircle, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/quotes/${quoteId}`);
      toast.success('Quote deleted successfully');
      // Refresh the quotes list
      fetchQuotes();
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast.error('Failed to delete quote');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading quotes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="container mx-auto max-w-7xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Quote Dashboard</CardTitle>
            <CardDescription>
              View all customer quote requests for One Clean Garage
            </CardDescription>
          </CardHeader>
          <CardContent>
            {quotes.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-500 text-lg">No quotes submitted yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Appointment</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Garage Size</TableHead>
                      <TableHead>Services</TableHead>
                      <TableHead>Base Price</TableHead>
                      <TableHead>Additional</TableHead>
                      <TableHead className="text-right">Total Price</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotes.map((quote) => (
                      <TableRow key={quote.id}>
                        <TableCell className="text-sm text-slate-600">
                          {formatDate(quote.createdAt)}
                        </TableCell>
                        <TableCell>
                          {quote.appointmentDate && quote.appointmentTime ? (
                            <div className="space-y-1">
                              <div className="font-medium text-emerald-700">
                                {new Date(quote.appointmentDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                              </div>
                              <div className="text-sm text-slate-600">{quote.appointmentTime}</div>
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              Not Scheduled
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {quote.name && <div className="font-semibold text-slate-900">{quote.name}</div>}
                            <div className="font-medium text-slate-700">{quote.email}</div>
                            <div className="text-sm text-slate-600">{quote.phone}</div>
                            {quote.zipCode && (
                              <div className="text-xs text-slate-500">
                                ZIP: {quote.zipCode}
                                {quote.distance && ` (${quote.distance.toFixed(1)} mi)`}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {quote.garageSize}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {quote.heavyItems && (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Heavy Items
                              </Badge>
                            )}
                            {quote.sweeping && (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Sweeping
                              </Badge>
                            )}
                            {quote.pressureWashing && (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Pressure Wash
                              </Badge>
                            )}
                            {!quote.heavyItems && !quote.sweeping && !quote.pressureWashing && (
                              <span className="text-sm text-slate-500">None</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          ${quote.basePrice.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-emerald-600">
                          +${quote.additionalCost.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-bold text-lg">
                          ${quote.totalPrice.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteQuote(quote.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex justify-between items-center">
                <p className="text-sm text-slate-600">
                  Total Quotes: <span className="font-semibold">{quotes.length}</span>
                </p>
                <p className="text-sm text-slate-600">
                  Total Revenue: <span className="font-semibold text-emerald-600 text-lg">
                    ${quotes.reduce((sum, quote) => sum + quote.totalPrice, 0).toFixed(2)}
                  </span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Admin;
